import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  AppButton,
  AppDialog,
  AppDialogBody,
  AppDialogContent,
  AppDialogDescription,
  AppDialogFooter,
  AppDialogHeader,
  AppDialogTitle,
  AppTextArea,
  AppTextInput,
  useToastV2
} from '@easyllama/ui-components'
import { useApp } from '~/context/appContext'
import { sendKnowledgeBaseMessage } from '~/service/Billing/fetchApiClient'

// Explicit values: with no <form> here, a bare reset() cannot clear uncontrolled inputs.
const EMPTY_FORM = { subject: '', message: '' }

const schema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  message: z.string().min(1, 'Message is required')
})

const ContactSalesDialog = ({
  open,
  handleOpenChange,
  dialogTitle = 'Contact sales',
  title = 'Have questions?',
  description = 'Our sales team is available during business hours and usually answers within 1 to 2 days. Fill out this form and we will get back to you as soon as possible.',
}) => {
  const toast = useToastV2()
  const { company, jwt, csrf, currentUserEmail } = useApp()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: zodResolver(schema)
  })

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      await sendKnowledgeBaseMessage(company.id, jwt, csrf, data.subject, data.message)
      toast.success('Message sent successfully', { showCloseButton: false })
      reset(EMPTY_FORM)
      handleOpenChange(false)
    } catch (error) {
      toast.critical(error.message || 'Failed to send message', { showCloseButton: false })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCancel = () => {
    reset(EMPTY_FORM)
    handleOpenChange(false)
  }

  return (
    <AppDialog open={open} onOpenChange={handleOpenChange}>
      <AppDialogContent className=' text-llama-grey-7'>
        <AppDialogHeader>
          <AppDialogTitle>{dialogTitle}</AppDialogTitle>
          <AppDialogDescription className='sr-only'>{`${dialogTitle} dialog`}</AppDialogDescription>
        </AppDialogHeader>
        <AppDialogBody>
          <div className='text-base font-bold text-llama-grey-10'>{title}</div>
          <div className='mt-6 text-sm font-normal text-llama-grey-10'>
            {description}
          </div>
          <div className='my-4 text-sm'>From: <span className='font-semibold'>{currentUserEmail}</span></div>
          <div className='flex flex-col gap-4'>
            <AppTextInput
              label='Subject'
              placeholder='Type here'
              required
              error={errors.subject?.message}
              {...register('subject')}
            />
            <AppTextArea
              label='Your message'
              placeholder='Type here'
              required
              error={errors.message?.message}
              {...register('message')}
            />
          </div>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant='secondary' onClick={onCancel}>
            Cancel
          </AppButton>
          <AppButton type='button' onClick={handleSubmit(onSubmit)} disabled={isSubmitting} className='disabled:cursor-not-allowed'>
            {isSubmitting ? 'Sending...' : 'Submit'}
          </AppButton>
        </AppDialogFooter>
      </AppDialogContent>
    </AppDialog>
  )
}

export default ContactSalesDialog
